import React from 'react';
import { Badge, Card } from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';
import ClickableTextWithModals from './ClickableTextWithModals';
import type { PostCodingChecks } from '../types/codingData.types';

interface PostCodingChecksCardProps {
  checks: PostCodingChecks;
  onOpenModal?: (dataType: string) => void;
}

const PostCodingChecksCard: React.FC<PostCodingChecksCardProps> = ({
  checks,
  onOpenModal,
}) => {
  // Define the check type with name
  type CheckWithName = {
    name: string;
    active: boolean;
    title: string;
    user_message: string;
    severity: 'HIGH' | 'MEDIUM' | 'LOW' | 'INFO';
  };

  // Convert PostCodingChecks object to array with check names
  const checksArray: CheckWithName[] = Object.entries(checks).map(
    ([name, check]) => ({
      name,
      ...check,
    })
  );

  // Get only active checks and sort by severity
  const severityOrder = { HIGH: 0, MEDIUM: 1, LOW: 2, INFO: 3 };
  const activeChecks = checksArray
    .filter(check => check.active)
    .sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

  // Severity configuration matching old dashboard colors
  const severityConfig = {
    HIGH: {
      color: '#dc2626', // red-600
      bgColor: 'bg-red-50',
      borderColor: '#fee2e2',
    },
    MEDIUM: {
      color: '#f59e0b', // amber-500
      bgColor: 'bg-amber-50',
      borderColor: '#fef3c7',
    },
    LOW: {
      color: '#3b82f6', // blue-500
      bgColor: 'bg-blue-50',
      borderColor: '#dbeafe',
    },
    INFO: {
      color: '#6b7280', // gray-500
      bgColor: 'bg-gray-50',
      borderColor: '#f3f4f6',
    },
  };

  // If no active checks, return null (don't show the section)
  if (activeChecks.length === 0) {
    return null;
  }

  return (
    <div className='flex flex-col gap-4'>
      {activeChecks.map(check => {
        const config = severityConfig[check.severity];

        return (
          <Card key={check.name}>
            {/* Header */}
            <div className='flex items-center gap-3 mb-3'>
              {/* Icon */}
              <AlertTriangle className='w-4 h-4 text-red-600' />

              {/* Title */}
              <h5
                className='text-sm flex-1 leading-tight'
                style={{ color: config.color }}
              >
                {check.title}
              </h5>

              {/* Severity Badge */}
              <Badge
                className='text-white'
                style={{
                  background: `linear-gradient(135deg, ${config.color} 0%, ${config.color}dd 100%)`,
                }}
              >
                {check.severity}
              </Badge>
            </div>

            {/* Message */}
            {check.user_message && (
              <div
                className='pt-3 border-t text-sm leading-relaxed text-gray-700'
                style={{ borderColor: config.borderColor }}
              >
                {onOpenModal ? (
                  <ClickableTextWithModals
                    text={check.user_message}
                    onOpenModal={onOpenModal}
                    size='sm'
                    className='text-gray-700'
                  />
                ) : (
                  <div className='whitespace-pre-wrap'>
                    {check.user_message}
                  </div>
                )}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default PostCodingChecksCard;
